// Player detail data formatter — pure functions that transform player summary
// API responses into display-ready table data.
//
// Follows the same pattern as the dashboard formatter: API data in, plain objects
// out, no React dependencies. This makes it testable and shareable between the
// screen and print layouts.
import type {
  PlayerSessionSummaryResponse,
  PlayerSummaryStats,
  ReportingSession
} from '@frontend/core/lib/domains/reporting/types'
import type { ColumnDef, ReportingSportConfig } from '../../lib/sport-config'
import { getStatByAccessor } from '../../lib/stat-utils'

// --- Types ---

export interface DrillRow {
  id: string
  name: string
  duration: string
  stats: PlayerSummaryStats
}

export interface PlayerTableData {
  rows: DrillRow[]
  totals: PlayerSummaryStats | null
  totalDuration: string
}

// --- Builders ---

/**
 * Build drill/period rows from the player summary's timePeriodStats.
 * Pure function — no React dependencies.
 */
export function buildDrillRows(
  playerSummary: PlayerSessionSummaryResponse | undefined,
  session: ReportingSession | null | undefined,
  config: ReportingSportConfig
): DrillRow[] {
  if (!playerSummary?.timePeriodStats) return []

  return playerSummary.timePeriodStats.map((tp, i) => {
    const drill = session?.drills?.find((d) => d.id === tp.drillId)
    const period = session?.periods?.find(
      (p) => p.periodNumber === tp.periodNumber
    )

    let name = `Period ${i + 1}`
    let id = `period-${i}`
    if (drill) {
      name = drill.name
      id = drill.id
    } else if (period) {
      name = config.formatPeriodName(
        period.periodNumber ?? i + 1,
        period.overtime
      )
      id = period.id
    }

    return {
      id,
      name,
      duration: config.formatDuration(
        getStatByAccessor(tp.summary, 'totalDuration_s') ?? 0
      ),
      stats: tp.summary
    }
  })
}

/**
 * Build the full table data: rows + totals.
 */
export function buildTableData(
  playerSummary: PlayerSessionSummaryResponse | undefined,
  session: ReportingSession | null | undefined,
  config: ReportingSportConfig
): PlayerTableData {
  const rows = buildDrillRows(playerSummary, session, config)
  const totals = playerSummary?.totals ?? null
  const totalDuration = totals
    ? config.formatDuration(getStatByAccessor(totals, 'totalDuration_s') ?? 0)
    : '00:00:00'

  return { rows, totals, totalDuration }
}

/**
 * Filter drill rows by drill ID. Returns all rows if drillId is empty/null.
 */
export function filterByDrill(
  rows: DrillRow[],
  drillId: string | null
): DrillRow[] {
  if (!drillId) return rows
  return rows.filter((r) => r.id === drillId)
}

/**
 * Build drill options for the filter dropdown from session data.
 */
export function buildDrillOptions(
  session?: ReportingSession | null
): { value: string; label: string }[] {
  if (!session?.drills?.length) return []
  return session.drills.map((d) => ({ value: d.id, label: d.name }))
}

/**
 * Filter players by position. Empty filterValue and the synthetic "All" filter
 * (canonical: []) both return every player. For real filters, the player's raw
 * position string is canonicalised via the sport's resolver and compared
 * against the filter's canonical list.
 */
export function filterByPosition<T extends { position: string | null }>(
  players: T[],
  filterValue: string,
  config: ReportingSportConfig
): T[] {
  if (!filterValue) return players
  const filter = config.positionFilters.find((f) => f.value === filterValue)
  if (!filter || filter.canonical.length === 0) return players
  const allowed = new Set<string>(filter.canonical)
  return players.filter((p) => {
    const canonical = config.resolvePosition(p.position)
    return canonical != null && allowed.has(canonical)
  })
}

/**
 * Resolve active columns from sport config tab groups.
 */
export function resolveColumns(
  tabGroups: { key: string; tabs: { key: string; columns: ColumnDef[] }[] }[],
  groupKey: string,
  tabKey: string
): ColumnDef[] {
  const group = tabGroups.find((g) => g.key === groupKey)
  if (!group) return []
  const tab = group.tabs.find((t) => t.key === tabKey)
  return tab?.columns ?? []
}
